import { Avatar, Box, Flex, HStack, VStack } from '@chakra-ui/react';
import { PropsWithChildren, useMemo } from 'react';
import { useIframePostMessage } from '../hooks/useIframePostMessage';
import { Link } from 'react-router-dom';

import { useTheme } from '../contexts/ThemeProvider';
import { useHasUnreadNotifications } from '@/service/club/hooks';
import { IBottomMenuLink, BottomMenu } from '../components/organisms/BottomMenu';
import Home from '../assets/home.svg?react';
import Tag from '../assets/Tag.svg?react';
import Gift from '../assets/Gift.svg?react';
// import Parter from '../assets/partner.svg?react';
import User from '../assets/user.svg?react';
import Notificacao from '../assets/Notificacao.svg?react';
import Grid from '../assets/Grid.svg?react';
import Mail from '../assets/Mail.svg?react';
import { FaBook, FaChild, FaNewspaper } from 'react-icons/fa';
import { ScrollToTop } from '../utils';
import { useProfile } from '../contexts/ProfileProvider';
import { TopMenu } from '../components/organisms/TopMenu';

export function MainTemplate({
  hideTopMenu = false,
  isNative = false,
  hideBottomMenu = false,
  noShadow = true,
  isIframeActive,
  children,
  goBack,
}: {
  isNative?: boolean;
  hideTopMenu?: boolean;
  hideBottomMenu?: boolean;
  noShadow?: boolean;
  isIframeActive?: boolean;
  goBack?: () => void;
} & PropsWithChildren) {
  const theme = useTheme();
  const { isFullscreen } = useIframePostMessage();

  const checkUrl = () => {
    if (typeof theme?.customerCentralModule === 'string' && theme?.customerCentralModule === 'true') return true;

    if (theme?.customerCentralModule === true) return true;

    return false;
  };

  const { profile, subscriptions } = useProfile();
  const { data: unreadData } = useHasUnreadNotifications(!!profile);
  const hasUnreadNotifications = !!unreadData?.hasUnread;

  const disableLibrary = theme?.disableLibraryModule;

  const hasBiblioteca =
    !disableLibrary &&
    theme?.productBenefits?.some(
      (p: any) => p.default_product?.is_bookstore && p.metadata?.hasMagazine?.value !== 'true'
    );
  const hasBanca =
    !disableLibrary && theme?.productBenefits?.some((p: any) => p.metadata?.hasMagazine?.value === 'true');
  const hasLeitor = hasBiblioteca || hasBanca;
  // Kids é um acervo à parte da biblioteca: só aparece para quem assina esse produto
  const kidsProductId = theme?.bookstoreModules?.kids?.productId;
  const hasKids =
    !disableLibrary && !!kidsProductId && subscriptions.some((sub) => Number(sub.plan?.product) === kidsProductId);

  const links = useMemo<IBottomMenuLink[]>(() => {
    const shouldShowDiscounts = theme?.ecommerceModule || theme?.localModule;

    if (profile && hasLeitor) {
      // New menu when biblioteca module is active
      return [
        ...(hasBiblioteca ? [{ name: 'Biblioteca', icon: <FaBook size={20} />, path: '/biblioteca' }] : []),
        ...(hasKids
          ? [{ name: 'Kids', icon: <FaChild size={20} />, path: `/biblioteca?produto=${kidsProductId}` }]
          : []),
        ...(hasBanca ? [{ name: 'Banca', icon: <FaNewspaper size={20} />, path: '/banca' }] : []),
        { name: 'Serviços', icon: <Gift />, path: '/produtos' },
        ...(checkUrl() ? [{ name: 'Central', icon: <Grid />, path: '/central-do-cliente' }] : []),
      ];
    }

    return profile
      ? [
          { name: 'Home', icon: <Home />, path: '/' },
          ...(shouldShowDiscounts ? [{ name: 'Descontos', icon: <Tag />, path: '/vantagens' }] : []),
          { name: 'Produtos', icon: <Gift />, path: '/produtos' },
          ...(checkUrl() ? [{ name: 'Central', icon: <Grid />, path: '/central-do-cliente' }] : []),
        ]
      : [
          { name: 'Contato', icon: <Mail />, path: '/contato', desktopName: 'Fale Conosco', hide: true },
          { name: 'Login', icon: <User />, path: '/login' },
        ];
  }, [profile, hasLeitor, hasBiblioteca, hasKids, kidsProductId, hasBanca, theme?.ecommerceModule, theme?.localModule]);
  const basePaddingTop = useMemo(() => {
    if (isFullscreen) return '0px';
    let result = 0;
    if (!hideTopMenu) result += isNative ? 30 : 60;

    return result + 'px';
  }, [hideTopMenu, isFullscreen, theme]);

  return (
    <>
      <ScrollToTop />
      {isFullscreen && (
        <style>{`
          iframe {
            position: fixed !important;
            top: 0 !important;
            left: 0 !important;
            width: 100vw !important;
            height: 100vh !important;
            z-index: 9999 !important;
            border: none !important;
          }
        `}</style>
      )}
      <Flex zIndex={200} pos={'fixed'} display={isFullscreen ? 'none' : { base: hideTopMenu ? 'none' : 'flex', lg: 'flex' }}>
        <TopMenu
          logo={theme.logo}
          colors={theme.colors}
          noShadow={noShadow}
          links={links.map((l) => ({
            display: l.desktopName || l.name,
            icon: l.icon,
            path: l.path,
            showOnTopMenu: true,
            state: l.state,
            ...(l.children
              ? {
                  children: l.children.map((c) => ({
                    display: c.name,
                    icon: c.icon,
                    path: c.path,
                    state: c.state,
                  })),
                }
              : {}),
          }))}
          goBack={goBack}
          menuHeight={isNative ? '30px' : '60px'}
          avatarComponent={
            <HStack>
              <Link
                style={{
                  zIndex: 200,
                  position: 'relative',
                }}
                to="/notificacoes"
              >
                <Notificacao
                  color={theme.colors.primaryColor}
                  style={{
                    width: '30px',
                    height: '30px',
                  }}
                />
                {hasUnreadNotifications && (
                  <Box
                    position="absolute"
                    top="0px"
                    right="0px"
                    width="10px"
                    height="10px"
                    borderRadius="full"
                    backgroundColor="#E53E3E"
                    border="2px solid white"
                  />
                )}
              </Link>
              <Link to="/perfil">
                <Avatar boxSize={isNative ? '25px' : '36px'} src={profile?.image} />
              </Link>
            </HStack>
          }
          logoCenter
          isNative={isNative}
          logoWidth={isNative ? '30px' : '35px'}
          backgroundColor={'#F1F4F5'}
        ></TopMenu>
      </Flex>
      <VStack
        maxWidth={{
          base: '100%',
          sm: '100%',
          lg: '1264px',
        }}
        paddingX={{ base: '0px', lg: '20px' }}
        margin="auto"
        paddingTop={isFullscreen ? '0px' : { base: basePaddingTop, lg: '60px' }}
        overflowY={'visible'}
        display={'flex'}
      >
        {children}
      </VStack>
      {!hideBottomMenu && !isFullscreen && <BottomMenu isIframeActive={isIframeActive} links={links.filter((l) => !l.hide)} />}
    </>
  );
}
